import React from 'react';
import {
  Card,
  CardFooter,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';
import { ReferralItem } from '../../models/referralModel';
import { IconInbox } from '@tabler/icons-react';
import {
  renderAccountCreatedBadge,
  renderLineBadge,
  renderStatusBadge,
} from './referralBadgeStyles';

interface Props {
  items: ReferralItem[];
  isLoading: boolean;
  onViewDetail: (referrerProfileId: string) => void;
  onRegisterProspect?: (item: ReferralItem) => void;
  registeringId?: string | null;
  paginationComponent?: React.ReactNode;
}

const formatDate = (dateString: string) => {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    const day = date.getDate();
    const months = [
      'ene',
      'feb',
      'mar',
      'abr',
      'may',
      'jun',
      'jul',
      'ago',
      'sep',
      'oct',
      'nov',
      'dic',
    ];
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return dateString;
  }
};

const ReferralListTable: React.FC<Props> = ({
  items,
  isLoading,
  onViewDetail,
  paginationComponent,
}) => {
  return (
    <Card
      className="border-0 mb-0"
      style={{
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.04)',
      }}
    >
      <Table className="align-items-center table-flush mb-0" responsive hover>
        <thead className="thead-light">
          <tr>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Referidor
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Prospecto
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
                textAlign: 'center',
                width: '115px',
              }}
            >
              Línea
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
                textAlign: 'center',
                width: '135px',
              }}
            >
              Estado
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Responsable
            </th>
            <th
              scope="col"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
              }}
            >
              Fecha
            </th>
            <th
              scope="col"
              className="text-right"
              style={{
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontWeight: 600,
                color: '#8898aa',
                width: '80px',
              }}
            >
              Acciones
            </th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={7} className="text-center py-4">
                <div
                  className="spinner-border spinner-border-sm mr-2"
                  style={{ color: '#00c389' }}
                  role="status"
                />
                <span className="text-muted font-weight-500" style={{ fontSize: '13px' }}>
                  Cargando referidos...
                </span>
              </td>
            </tr>
          )}

          {!isLoading && items.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center py-5">
                <div className="text-muted">
                  <IconInbox size={36} stroke={1.5} className="mb-2 text-gray" />
                  <p className="mb-0 font-weight-bold" style={{ fontSize: '14px' }}>
                    No se encontraron referidos
                  </p>
                  <small className="text-muted">Intenta ajustar los filtros de búsqueda</small>
                </div>
              </td>
            </tr>
          )}

          {!isLoading &&
            items.map((item) => {
              const referrerName = item.referrerProfile
                ? `${item.referrerProfile.first_name} ${item.referrerProfile.last_name}`.trim()
                : 'Sin nombre';
              const referrerPhone = item.referrerProfile?.phone
                ? `${item.referrerProfile.area_code || '+503'} ${item.referrerProfile.phone}`
                : '—';

              const prospectName =
                `${item.referred_first_name || ''} ${item.referred_last_name || ''}`.trim() ||
                'Sin nombre';
              const prospectPhone = item.referred_phone
                ? `${item.referred_phone_area_code || '+503'} ${item.referred_phone}`
                : '—';

              const responsibleName = item.assignedResponsible
                ? `${item.assignedResponsible.first_name} ${item.assignedResponsible.last_name}`.trim()
                : '—';

              return (
                <tr key={item.id}>
                  {/* REFERIDOR */}
                  <td>
                    <span className="font-weight-600 text-dark" style={{ fontSize: '13px' }}>
                      {referrerName}
                    </span>
                    <div className="text-muted" style={{ fontSize: '11px' }}>
                      {referrerPhone}
                    </div>
                  </td>

                  {/* PROSPECTO */}
                  <td>
                    <div className="d-flex align-items-center flex-wrap" style={{ gap: '6px' }}>
                      <span className="font-weight-600 text-dark" style={{ fontSize: '13px' }}>
                        {prospectName}
                      </span>
                      {item.referred_profile_id && renderAccountCreatedBadge()}
                    </div>
                    <div className="text-muted" style={{ fontSize: '11px' }}>
                      {prospectPhone}
                    </div>
                  </td>

                  {/* LÍNEA (Badge estandarizado) */}
                  <td className="text-center">{renderLineBadge(item.line)}</td>

                  {/* ESTADO (Badge estandarizado) */}
                  <td className="text-center">
                    {renderStatusBadge(item.status, item.line)}
                  </td>

                  {/* RESPONSABLE */}
                  <td className="text-dark font-weight-500" style={{ fontSize: '12px' }}>
                    {responsibleName}
                  </td>

                  {/* FECHA */}
                  <td className="text-muted" style={{ fontSize: '12px' }}>
                    {formatDate(item.created_at)}
                  </td>

                  {/* ACCIONES */}
                  <td className="text-right">
                    <UncontrolledDropdown>
                      <DropdownToggle
                        className="btn-icon-only text-light"
                        role="button"
                        size="sm"
                        color=""
                        onClick={(e) => e.preventDefault()}
                      >
                        <i className="fas fa-ellipsis-v text-muted" />
                      </DropdownToggle>
                      <DropdownMenu className="dropdown-menu-arrow" right container="body">
                        <DropdownItem
                          onClick={() => onViewDetail(item.referrer_profile_id)}
                        >
                          Ver detalle
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </td>
                </tr>
              );
            })}
        </tbody>
      </Table>

      {paginationComponent && (
        <CardFooter
          className="py-2 border-0 d-flex justify-content-end bg-white"
          style={{ borderRadius: '0 0 10px 10px' }}
        >
          {paginationComponent}
        </CardFooter>
      )}
    </Card>
  );
};

export default ReferralListTable;
